import React, { useState, useEffect, useCallback } from "react";
import { useAccess } from "hooks/useAccess";
import {
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  Box,
  Typography,
  InputAdornment,
  SelectChangeEvent,
  Chip,
  Avatar,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  PersonAdd as PersonAddIcon,
  SupervisorAccount as SupervisorAccountIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Search as SearchIcon,
  People as PeopleIcon,
} from "@mui/icons-material";
import axiosInstance from "utilities/axios";
import UserCreateDialog from "components/dialogs/UserCreateDialog";
import UserAuditLogDialog from "components/dialogs/UserAuditLogDialog";
import DataTable, { ColumnDef } from "components/common/DataTable";
import ProtectedPage from "components/common/ProtectedPage";
import Can from "components/common/Can";
import { formatUserForDisplay, USER_TYPE_CHIP, PERMISSION_LABEL } from "utils/userFormatting";

type AgencyDepartment = { _id: string; title: string };
type GeneralInfo = { banks: any[]; agency_departments: AgencyDepartment[] };
type User = { _id: string; name: string; email: string; permissions: string[]; [key: string]: any };

function getInitials(name: string) {
  return name
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0] || "")
    .join("")
    .toUpperCase();
}

function Users() {
  const [loading, setLoading]               = useState(false);
  const [users, setUsers]                   = useState<User[]>([]);
  const [total, setTotal]                   = useState(0);
  const [page, setPage]                     = useState(1);
  const [rowsPerPage, setRowsPerPage]       = useState(30);
  const [search, setSearch]                 = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterUserType, setFilterUserType] = useState("");

  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [editUserDialogOpen, setEditUserDialogOpen]     = useState(false);
  const [auditLogDialogOpen, setAuditLogDialogOpen]     = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit]   = useState<User | null>(null);
  const [selectedUserForAudit, setSelectedUserForAudit] = useState<User | null>(null);

  const [confirmUserOpen, setConfirmUserOpen]   = useState(false);
  const [confirmUser, setConfirmUser]           = useState<User | null>(null);
  const [togglingUser, setTogglingUser]         = useState(false);

  const [generalFilterData, setGeneralFilterData] = useState<GeneralInfo>({ banks: [], agency_departments: [] });
  const { isMonitoring, isAdmin, isHead } = useAccess();

  useEffect(() => {
    axiosInstance.get("/general_info").then((resp) => setGeneralFilterData(resp.data)).catch(console.error);
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  const fetchUsers = useCallback(() => {
    setLoading(true);
    axiosInstance
      .post("/fetch_users", { page: page - 1, limit: rowsPerPage, search: debouncedSearch, userType: filterUserType || undefined })
      .then((res) => {
        const responseData = res.data.users || res.data;
        const updatedUsers = (Array.isArray(responseData) ? responseData : []).map((result: any) => {
          const fmt = formatUserForDisplay(result);
          return fmt.permissions?.includes("USER_MANAGE")
            ? { ...fmt, isAdmin: true }
            : fmt;
        });
        setUsers(updatedUsers);
        setTotal(res.data.total ?? updatedUsers.length);
        setLoading(false);
      })
      .catch((err) => { console.error(err); setLoading(false); });
  }, [page, rowsPerPage, debouncedSearch, filterUserType]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  async function handleToggleUser() {
    if (!confirmUser) return;
    setTogglingUser(true);
    try {
      await axiosInstance.post("/auth/deactivation", { userId: confirmUser._id, deactivate: !confirmUser.deactivated });
      setConfirmUserOpen(false);
      setConfirmUser(null);
      fetchUsers();
    } catch (err: any) {
      alert(err?.response?.data?.message || "შეცდომა");
    } finally {
      setTogglingUser(false);
    }
  }

  const showTypeFilter = !isMonitoring;

  const columns: ColumnDef<User>[] = [
    {
      id: "avatar",
      label: "",
      width: 44,
      render: (row) => (
        <Avatar sx={{ width: 30, height: 30, fontSize: 12, bgcolor: row.isAdmin ? "primary.main" : row.user_type === "monitoring" ? "secondary.main" : "grey.400" }}>
          {row.isAdmin ? <SupervisorAccountIcon sx={{ fontSize: 16 }} /> : getInitials(row.name || "")}
        </Avatar>
      ),
    },
    {
      id: "name",
      label: "სახელი",
      render: (row) => (
        <>
          <Typography variant="body2" fontWeight={500}>{row.name}</Typography>
          {row.department?.title && (
            <Typography variant="caption" color="text.secondary">{row.department.title}</Typography>
          )}
        </>
      ),
    },
    {
      id: "email",
      label: "ელ-ფოსტა",
      render: (row) => <Typography variant="body2" color="text.secondary">{row.email}</Typography>,
    },
    {
      id: "org",
      label: "ორგანიზაცია",
      align: "center",
      render: (row) => <Typography variant="body2">{row.bankDisplay}</Typography>,
    },
    {
      id: "type",
      label: "ტიპი",
      align: "center",
      render: (row) =>
        row.user_type && USER_TYPE_CHIP[row.user_type] ? (
          <Chip
            label={USER_TYPE_CHIP[row.user_type].label}
            color={USER_TYPE_CHIP[row.user_type].color}
            size="small"
            variant="outlined"
            sx={{ fontSize: 11, height: 20 }}
          />
        ) : null,
    },
    {
      id: "permissions",
      label: "უფლებები",
      render: (row) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {(row.permissions || []).map((p: string) => (
            <Chip
              key={p}
              label={PERMISSION_LABEL[p] ?? p}
              size="small"
              variant="outlined"
              color="default"
              sx={{ fontSize: 10, height: 18 }}
            />
          ))}
        </Stack>
      ),
    },
    {
      id: "status",
      label: "სტატუსი",
      align: "center",
      render: (row) => (
        <Chip
          label={row.deactivated ? "დეაქტივირებული" : "აქტიური"}
          color={row.deactivated ? "error" : "success"}
          size="small"
          variant="outlined"
          disabled={isMonitoring && isHead && row.deactivated}
          onClick={
            (isAdmin || row._canManage)
              ? () => { setConfirmUser(row); setConfirmUserOpen(true); }
              : undefined
          }
          sx={{ fontSize: 11, height: 20, ...((isAdmin || row._canManage) ? { cursor: "pointer" } : {}) }}
        />
      ),
    },
    {
      id: "actions",
      label: "",
      width: 72,
      align: "center",
      render: (row) => (
        <Can permission="USER_MANAGE">
          <Box display="flex" justifyContent="center" gap={0.25}>
            <Tooltip title="რედაქტირება">
              <IconButton
                size="small"
                onClick={() => { setSelectedUserForEdit(row); setEditUserDialogOpen(true); }}
                sx={{ p: 0.5, "& svg": { fontSize: 15 } }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="ცვლილებების ისტორია">
              <IconButton
                size="small"
                color="primary"
                onClick={() => { setSelectedUserForAudit(row); setAuditLogDialogOpen(true); }}
                sx={{ p: 0.5, "& svg": { fontSize: 15 } }}
              >
                <HistoryIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Can>
      ),
    },
  ];

  return (
    <ProtectedPage permission="USER_MANAGE">
      <Box sx={{ p: 3 }}>
        <UserCreateDialog open={createUserDialogOpen} setOpen={setCreateUserDialogOpen} isMonitoring={isMonitoring} agencyDepartments={generalFilterData?.agency_departments} onUserUpdated={fetchUsers} />
        <UserCreateDialog open={editUserDialogOpen} setOpen={setEditUserDialogOpen} isMonitoring={isMonitoring} agencyDepartments={generalFilterData?.agency_departments} editUser={selectedUserForEdit} onUserUpdated={() => { fetchUsers(); setSelectedUserForEdit(null); }} />
        <UserAuditLogDialog open={auditLogDialogOpen} onClose={() => { setAuditLogDialogOpen(false); setSelectedUserForAudit(null); }} userId={selectedUserForAudit?._id || ""} userName={selectedUserForAudit?.name || ""} />

        {/* Page header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={2}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <PeopleIcon color="primary" sx={{ fontSize: 28 }} />
            <Typography variant="h5" fontWeight={700}>მომხმარებლები</Typography>
            {total > 0 && (
              <Chip label={total} size="small" color="primary" variant="outlined" sx={{ fontWeight: 600, height: 22, fontSize: 12 }} />
            )}
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
            {showTypeFilter && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>ტიპი</InputLabel>
                <Select value={filterUserType} label="ტიპი" onChange={(e) => { setFilterUserType(e.target.value); setPage(1); }}>
                  <MenuItem value=""><em>ყველა</em></MenuItem>
                  <MenuItem value="agency">სააგენტო</MenuItem>
                  <MenuItem value="monitoring">მონიტ. კომპანია</MenuItem>
                  <MenuItem value="admin">ადმინი</MenuItem>
                </Select>
              </FormControl>
            )}
            <Can permission="USER_MANAGE">
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <TextField
                  placeholder="ძებნა..."
                  size="small"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment> }}
                  sx={{ width: 220 }}
                />
                <Button
                  onClick={() => setCreateUserDialogOpen(true)}
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<PersonAddIcon fontSize="small" />}
                  disableElevation
                  sx={{ whiteSpace: "nowrap", fontWeight: 600, px: 2 }}
                >
                  დამატება
                </Button>
              </Stack>
            </Can>
          </Stack>
        </Box>

        <DataTable
          columns={columns}
          rows={users}
          rowKey={(row) => row._id}
          loading={loading}
          emptyText="მომხმარებლები არ მოიძებნა"
          pagination={{
            page,
            count: Math.ceil(total / rowsPerPage),
            rowsPerPage,
            onPageChange: (_, v) => setPage(v),
            onRowsPerPageChange: (e: SelectChangeEvent<number>) => { setRowsPerPage(e.target.value as number); setPage(1); },
          }}
        />

        {/* Confirm user toggle dialog */}
        <Dialog open={confirmUserOpen} onClose={() => { setConfirmUserOpen(false); setConfirmUser(null); }} maxWidth="xs" fullWidth>
          <DialogTitle>დადასტურება</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              დარწმუნებული ხართ, რომ გსურთ{" "}
              <strong>{confirmUser?.name}</strong>{" "}
              მომხმარებლის {confirmUser?.deactivated ? "გააქტიურება" : "დეაქტივაცია"}?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setConfirmUserOpen(false); setConfirmUser(null); }}>გაუქმება</Button>
            {!(isMonitoring && isHead && confirmUser?.deactivated) && (
              <Button
                variant="contained"
                color={confirmUser?.deactivated ? "success" : "error"}
                disabled={togglingUser}
                onClick={handleToggleUser}
              >
                {confirmUser?.deactivated ? "გააქტიურება" : "დეაქტივაცია"}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </ProtectedPage>
  );
}

export default Users;
